import { I18n } from "@assets/strings";
import React from "react";
import styled, { css } from "styled-components";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { DetailedPerson } from "@domain/user/user";
import { useAsync } from "@utils/hooks";
import { theme } from "@ui/styles/theme";
import { mobile } from "@ui/styles/utils";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { FollowIcon, UnfollowIcon } from "@ui/components/icons";
import { row } from "@ui/components/layout";

interface FollowButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	profile: DetailedPerson;
	isBig?: boolean;
	iconMode?: boolean;
	onToggle?: () => void;
	isOrg?: boolean;
}
export const FollowButton: React.FC<FollowButtonProps> = ({
	profile,
	isOrg,
	isBig,
	iconMode,
	onToggle,
	...htmlProps
}) => {
	const { userService } = useServices();
	const currentProfile = useCurrentProfile();
	const { followedByMeSince } = profile;
	const iconSize = iconMode ? 23 : 18;
	const { run, loading } = useAsync(async () => {
		await userService.toggleFollow(profile);
		onToggle?.();
	}, [profile]);
	if (!currentProfile) {
		return null;
	}
	return (
		<StyledButton
			iconMode={iconMode}
			loading={loading}
			buttonType={followedByMeSince ? "secondary" : "primary"}
			big={isBig}
			{...htmlProps}
			onClick={(e) => {
				e.preventDefault();
				e.stopPropagation();
				if (!loading) {
					run();
				}
			}}
		>
			{(isBig || iconMode) &&
				(followedByMeSince ? (
					<UnfollowIcon width={iconSize} height={iconSize} />
				) : (
					<StyledFollowIcon color={theme.colors.white} width={iconSize} height={iconSize} />
				))}
			{!iconMode && (
				<span>
					{I18n.t(
						isOrg
							? `organization.header.${followedByMeSince ? "unfollow" : "follow"}`
							: `profile.action.${followedByMeSince ? "unfollow" : "follow"}`
					)}
				</span>
			)}
		</StyledButton>
	);
};

const StyledButton = styled(LoadingButton).withConfig({
	shouldForwardProp: (prop) => !["iconMode", "big"].includes(prop),
})<{ iconMode?: boolean; big?: boolean }>`
	${row(8, "center", "center")};
	min-width: 50px;
	border-radius: ${({ big }) => (big ? 7 : 4)}px;
	padding: ${({ big }) => (big ? "10px 28px" : "4px 13px")};
	min-height: ${({ big }) => (big ? 34 : 23)}px;

	${({ iconMode }) =>
		iconMode &&
		css`
			border-radius: 26px;
			padding: 14px 13px;
		`}

	span {
		font-size: ${({ big }) => (big ? 0.9375 : 0.8125)}rem;
		font-weight: ${({ big }) => (big ? 700 : 600)};
	}
`;

const StyledFollowIcon = styled(FollowIcon)`
	@media ${mobile} {
		margin-right: 4px;
	}
`;
